<script setup lang="ts">
import type { RichTextSchema } from 'types/storyblok/rich-text'
import { textColorStyling } from 'lib/storyblok'
import xss from 'xss'

interface Props {
  story: RichTextSchema
  position?: number
}

const props = withDefaults(defineProps<Props>(), {
  position: -1,
})

const router = useRouter()
const { $url } = useNuxtApp()
const element = ref()

const textColor = computed(() => textColorStyling(props.story))
const textAlign = computed(() =>
  props.story.text_align ? { 'text-align': props.story.text_align } : {},
)

/**
 *
 * Replace all links in the richtext wrapped in the <a> tag with a <nuxt-link> tag and in the $url helper function
 * You're not able to use the <nuxt-link> tag inside v-html.
 * So we attach an eventlistener instead. See https://github.com/nuxt-community/markdownit-module/issues/16#issuecomment-363486760
 */
const parsedRichText = computed(() => {
  if (!props.story || !props.story.text)
    return ''

  let html = props.story.text.html
  if (!html)
    return ''
  const regex = /<a href="(.+?)">(.+?)<\/a>/g
  const matches = html.match(regex)
  if (matches) {
    matches.forEach((match) => {
      const href = match.match(/href="(.+?)"/)?.[1]
      const text = match.match(/>((.|\n)+?)</)?.[1]
      if (!href || !text)
        return
      if (href[0] !== '/')
        return

      const nuxtLink = `<a href="${$url(href)}">${text}</a>`
      const newText = html?.replace(match, nuxtLink)
      if (newText)
        html = newText
    })
  }
  return html
})

function navigate(event: any) {
  const href = event.target.getAttribute('href')
  if (href && href[0] === '/') {
    event.preventDefault()
    router.push(href)
  }
}

onMounted(() => {
  const links = element.value.getElementsByTagName('a')
  for (let i = 0; i < links.length; i++)
    links[i].addEventListener('click', navigate, false)
})

onBeforeUnmount(() => {
  const links = element.value.getElementsByTagName('a')
  for (let i = 0; i < links.length; i++)
    links[i].removeEventListener('click', navigate, false)
})
</script>

<template>
  <div
    v-editable="story"
    class="richText"
    :class="[
      story.desktop_indent,
      story.mobile_indent,
      story.margin,
      story.font_size,
      story.visibility,
    ]"
    :style="[textColor, textAlign, textColor]"
  >
    <div ref="element" v-html="xss(parsedRichText)" />
  </div>
</template>

<style scoped lang="scss">
@import 'assets/scss/legacy/_variables.scss';
.richText {
  position: relative;
  h1 {
    @include h-large;
  }
  h2 {
    @include h-medium;
  }
  h3 {
    @include h-small;
  }
  h4 {
    @include h-mini;
  }
  h5 {
    @include h-mini;
  }
  ul,
  ol {
    list-style-position: inside;
  }
  ul,
  ol,
  p {
    font-size: 1.6rem;
    line-height: 1.7em;
    letter-spacing: 0.02em;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0;
    margin-inline-end: 0;
    text-align: -webkit-match-parent;
    img {
      display: inline-block;
    }
    em {
      font-style: italic;
    }
    a {
      border-bottom-width: 0.1px;
      border-bottom-style: solid;
      border-bottom-color: inherit;
    }
    @media (hover: hover) {
      a:hover {
        border-bottom: 0.1px solid transparent;
      }
    }
  }
}
</style>
